import React, { useState } from 'react';
import { Badge, Button, Spinner } from 'reactstrap';

import { Toolbar } from '~components';

import { FormTemplate, FormTemplateVersion } from '~forms/types';

interface ComponentProps {
  template: FormTemplate;
  versions: FormTemplateVersion[];
  selectedVersionId: string | null;
  onSelectVersion: (versionId: string) => void;
  hasOpenDraft: boolean;
  canEdit: boolean;
  isPublishing: boolean;
  isCreatingVersion: boolean;
  onTitleBlur: (newTitle: string) => void;
  onPublish: () => void;
  onCreateVersion: () => void;
  onPreview: () => void;
  titleReadOnly: boolean;
}

const versionLabel = (version: FormTemplateVersion): string =>
  `v${version.version_number} - ${version.published_at ? 'published' : 'draft'}`;

const FormTopBar: React.FC<ComponentProps> = ({
  template,
  versions,
  selectedVersionId,
  onSelectVersion,
  hasOpenDraft,
  canEdit,
  isPublishing,
  isCreatingVersion,
  onTitleBlur,
  onPublish,
  onCreateVersion,
  onPreview,
  titleReadOnly,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [titleValue, setTitleValue] = useState(template.title);

  const handleTitleClick = () => {
    if (!titleReadOnly) {
      setIsEditing(true);
      setTitleValue(template.title);
    }
  };

  const handleTitleBlur = () => {
    setIsEditing(false);
    if (titleValue.trim() && titleValue.trim() !== template.title) {
      onTitleBlur(titleValue.trim());
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setTitleValue(template.title);
    }
  };

  // Versions newest-first for the selector.
  const orderedVersions = [...versions].sort((a, b) => b.version_number - a.version_number);
  const selectedVersion = versions.find((v) => v.id === selectedVersionId) ?? null;
  const selectedIsPublished = !!selectedVersion?.published_at;

  const previewButton = (
    <Button
      key="form-builder-preview-btn"
      color="info"
      outline
      size="sm"
      className="me-2"
      onClick={onPreview}
      data-cy="form-builder-preview-btn"
      aria-label="Preview form"
    >
      Preview
    </Button>
  );

  const publishButton = (
    <Button
      key="form-builder-publish-btn"
      color="success"
      size="sm"
      onClick={onPublish}
      disabled={isPublishing}
      data-cy="form-builder-publish-btn"
      aria-label="Publish form"
    >
      {isPublishing ? (
        <>
          <Spinner size="sm" className="me-1" />
          Publishing...
        </>
      ) : (
        'Publish'
      )}
    </Button>
  );

  const createVersionButton = (
    <Button
      key="form-builder-create-version-btn"
      color="success"
      size="sm"
      onClick={onCreateVersion}
      disabled={isCreatingVersion}
      data-cy="form-builder-create-version-btn"
      aria-label="Create new version"
    >
      {isCreatingVersion ? (
        <>
          <Spinner size="sm" className="me-1" />
          Creating...
        </>
      ) : (
        'Create New Version'
      )}
    </Button>
  );

  // Action button reflects the template's state: publish the open draft, or start a new version.
  const actionButtons = [previewButton];
  if (canEdit) {
    actionButtons.push(hasOpenDraft ? publishButton : createVersionButton);
  }

  const titleElement = isEditing ? (
    <input
      className="form-builder-title-input"
      value={titleValue}
      autoFocus
      onChange={(e) => setTitleValue(e.target.value)}
      onBlur={handleTitleBlur}
      onKeyDown={handleTitleKeyDown}
      aria-label="Form title"
      data-cy="form-builder-title-input"
    />
  ) : (
    <span
      className="form-builder-title-display"
      onClick={handleTitleClick}
      role={titleReadOnly ? undefined : 'button'}
      tabIndex={titleReadOnly ? undefined : 0}
      onKeyDown={
        titleReadOnly
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTitleClick();
              }
            }
      }
      aria-label={`Form title: ${template.title}`}
      data-cy="form-builder-title-display"
    >
      {template.title}
    </span>
  );

  return (
    <Toolbar
      id="form-builder-top-bar"
      leftCluster={
        <div className="d-flex align-items-center gap-2">
          {titleElement}
          <select
            className="form-select form-select-sm form-builder-version-select"
            value={selectedVersionId ?? ''}
            onChange={(e) => onSelectVersion(e.target.value)}
            aria-label="Select version"
            data-cy="form-builder-version-select"
          >
            {orderedVersions.map((version) => (
              <option key={version.id} value={version.id}>
                {versionLabel(version)}
              </option>
            ))}
          </select>
          <Badge color={selectedIsPublished ? 'success' : 'warning'} pill>
            {selectedIsPublished ? 'Published' : 'Draft'}
          </Badge>
        </div>
      }
      rightCluster={<>{actionButtons}</>}
    />
  );
};

export default FormTopBar;
